import { SelectOption } from '@common/types/common';
import { Flag } from '@components/display/images';
import { Select } from '@components/utility/selects';
import { useAppDispatch, useAppSelector } from '@hooks/generic';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { FormControl, SelectChangeEvent, Typography } from '@mui/material';
import { useGetLanguagesQuery } from '@services/translation';
import { changeLocale } from '@store/translationSlice';
import { FormattedMessage } from 'react-intl';

// Supported languages configuration
const FALLBACK_LANGUAGES: SelectOption[] = [{ value: 'en', label: 'English' }];

interface LanguageSelectorProps {
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
    showFlag?: boolean;
    showLabel?: boolean;
    className?: string;
    shadow?: boolean;
}

/**
 * @returns A dropdown as identical as possible to the one used in Keycloak for V1D
 */
const LanguageSelector = ({
    variant = 'outlined',
    size = 'small',
    showFlag = true,
    showLabel = false,
    className = '',
}: LanguageSelectorProps) => {
    const dispatch = useAppDispatch();
    const currentLocale = useAppSelector((state) => state.translation.locale);
    const isLoading = useAppSelector((state) => state.translation.loading);
    const { data: languages = FALLBACK_LANGUAGES, isLoading: isLanguagesLoading } = useGetLanguagesQuery();

    const handleLanguageChange = (event: SelectChangeEvent<unknown>) => {
        const newLocale = event.target.value as string;
        const newLocaleName = languages.find((sl) => sl.value === newLocale)?.label;
        dispatch(changeLocale({ locale: newLocale, localeName: newLocaleName }));
    };

    const currentLanguage = languages.find((lang) => lang.value === currentLocale) || languages[0];

    return (
        <FormControl variant={variant} size={size} className={className}>
            {showLabel && (
                <label className="text-sm font-medium text-gray-700 mb-1">
                    <FormattedMessage
                        id="common.language"
                        defaultMessage="Language"
                        description="Label for language selector"
                    />
                </label>
            )}
            <Select
                value={currentLocale}
                onChange={(event) => handleLanguageChange(event)}
                items={languages}
                disabled={isLoading || isLanguagesLoading}
                displayEmpty
                type="default"
                className="h-[41px]"
                outlinedInputProps={{
                    classes: {
                        notchedOutline: 'shadow-none',
                        input: 'min-h-0 pr-[42px]',
                    },
                }}
                IconComponent={(props) => <ArrowDropDownIcon {...props} sx={{ fill: 'black' }} />}
                renderValue={() => (
                    <div className="flex items-center gap-2">
                        {showFlag && <Flag flag={currentLanguage.value} />}
                        <span className="font-inter text-md">{currentLanguage.label}</span>
                    </div>
                )}
                renderOption={(option) => (
                    <div className="flex items-center gap-3">
                        {showFlag && <Flag flag={option.value as string} />}
                        <Typography>{option.label}</Typography>
                    </div>
                )}
            />
        </FormControl>
    );
};

export { LanguageSelector };
